import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { School } from '../../types/School';

type SchoolStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

export interface SchoolState {
  school: School | null;
  status: SchoolStatus;
  error: string | null;
}

const initialState: SchoolState = {
  school: null,
  status: 'idle',
  error: null,
};

export const schoolSlice = createSlice({
  name: 'school',
  initialState,
  reducers: {
    schoolLoading: (state) => {
      state.status = 'loading';
      state.error = null;
    },
    schoolReceived: (state, action: PayloadAction<School | null>) => {
      state.school = action.payload;
      state.status = 'succeeded';
      state.error = null;
    },
    schoolUpdateSucceeded: (state, action: PayloadAction<School>) => {
      state.school = action.payload;
      state.status = 'succeeded';
      state.error = null;
    },
    schoolFailed: (state, action: PayloadAction<string | null>) => {
      state.status = 'failed';
      state.error = action.payload ?? 'Failed to load school';
    },
    clearSchool: (state) => {
      state.school = null;
      state.status = 'idle';
      state.error = null;
    },
  },
  // Listen for logout action from authSlice to clear school data
  extraReducers: (builder) => {
    builder.addCase(logout, (state) => {
      state.school = null;
      state.status = 'idle';
      state.error = null;
    });
  },
});

export const {
  schoolLoading,
  schoolReceived,
  schoolUpdateSucceeded,
  schoolFailed,
  clearSchool,
} = schoolSlice.actions;

// Selectors
export const selectSchool = (state: { school: SchoolState }) =>
  state.school.school;
export const selectSchoolStatus = (state: { school: SchoolState }) =>
  state.school.status;
export const selectSchoolError = (state: { school: SchoolState }) =>
  state.school.error;

export default schoolSlice.reducer;

// Import the logout action from authSlice
import { logout } from '../auth/authSlice';
